/**
 * Authentication Utilities
 * 
 * Centralized authentication logic to ensure consistent auth checks
 * across all services and prevent duplication.
 */

import { getSupabase } from '../database';
import type { User } from '@supabase/supabase-js';

const supabase = getSupabase();

/**
 * Require authenticated user or throw error
 * 
 * Use this at the start of service methods that require authentication.
 * Provides consistent error messaging and reduces code duplication.
 * 
 * @throws Error if user is not authenticated
 * @returns The authenticated user
 * 
 * @example
 * ```typescript
 * static async createCategory(dto: CreateCategoryDto): Promise<UserCategory> {
 *     const user = await requireAuth();
 *     // ... rest of method
 * }
 * ```
 */
export async function requireAuth(): Promise<User> {
    const { data: { user }, error } = await supabase.auth.getUser();

    if (error) {
        throw new Error(`Authentication error: ${error.message}`);
    }

    if (!user) {
        throw new Error('Not authenticated. Please sign in to continue.');
    }

    return user;
}

/**
 * Get current user without throwing error
 * 
 * Use when you want to check auth status without forcing authentication.
 * Returns null if user is not authenticated.
 * 
 * @returns The authenticated user or null
 */
export async function getCurrentUser(): Promise<User | null> {
    const { data: { user } } = await supabase.auth.getUser();
    return user;
}

/**
 * Get user ID or throw error
 * 
 * Convenience method for methods that only need the user ID.
 * 
 * @throws Error if user is not authenticated
 * @returns User ID
 */
export async function requireUserId(): Promise<string> {
    const user = await requireAuth();
    return user.id;
}
